import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { MoreHorizontal } from "lucide-react";

import { cn } from "@/lib/utils";
import { useHoverPrefetch } from "@/hooks/useHoverPrefetch";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  activeNavId,
  isMobileMoreNavItem,
  isMobileNavItem,
  MOBILE_MORE_NAV_IDS,
  MOBILE_PRIMARY_NAV_IDS,
  useVisibleNavItems,
} from "./navItems";

function sortByNavSlotOrder<T extends { id: string }>(
  items: T[],
  order: readonly string[],
): T[] {
  const rank = new Map(order.map((id, index) => [id, index]));
  return [...items].sort(
    (a, b) => (rank.get(a.id) ?? 999) - (rank.get(b.id) ?? 999),
  );
}

interface MobileNavProps {
  className?: string;
}

/** Bottom navigation pill shown on small viewports (in document flow). */
export function MobileNav({ className }: MobileNavProps) {
  const { pathname } = useLocation();
  const visible = useVisibleNavItems();
  const primary = sortByNavSlotOrder(
    visible.filter((item) => isMobileNavItem(item.id)),
    MOBILE_PRIMARY_NAV_IDS,
  );
  const moreItems = sortByNavSlotOrder(
    visible.filter((item) => isMobileMoreNavItem(item.id)),
    MOBILE_MORE_NAV_IDS,
  );
  const prefetch = useHoverPrefetch();
  const [moreOpen, setMoreOpen] = useState(false);
  const moreActive = moreItems.some((item) => activeNavId(pathname) === item.id);

  return (
    <>
      <nav
        className={cn(
          "shell-chrome glass-pill clay glass-no-hover flex items-center justify-around gap-1 overflow-hidden px-1 py-1",
          className,
        )}
        aria-label="Primary"
      >
        {primary.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.id}
              to={item.path}
              end={item.path === "/"}
              aria-label={item.label}
              onPointerDown={() => prefetch.immediate(item.path)}
              className={({ isActive }) =>
                cn(
                  "flex min-h-11 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-1 text-xs",
                  isActive
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground",
                )
              }
            >
              <Icon className="size-5" />
              {item.label}
            </NavLink>
          );
        })}

        {moreItems.length > 0 && (
          <button
            type="button"
            aria-label="More"
            aria-expanded={moreOpen}
            aria-haspopup="dialog"
            onClick={() => setMoreOpen(true)}
            className={cn(
              "flex min-h-11 min-w-0 flex-1 flex-col items-center justify-center gap-0.5 rounded-lg px-1 text-xs",
              moreActive || moreOpen
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <MoreHorizontal className="size-5" />
            More
          </button>
        )}
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl pb-safe"
        >
          <SheetHeader className="mb-3">
            <SheetTitle>More</SheetTitle>
          </SheetHeader>
          <nav aria-label="More destinations" className="flex flex-col gap-1">
            {moreItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.id}
                  to={item.path}
                  aria-label={item.label}
                  onPointerDown={() => prefetch.immediate(item.path)}
                  onClick={() => setMoreOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex min-h-11 items-center gap-3 rounded-xl px-3 text-sm",
                      isActive
                        ? "bg-primary/10 font-medium text-primary"
                        : "text-foreground hover:bg-accent",
                    )
                  }
                >
                  <Icon className="size-5 shrink-0" />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </SheetContent>
      </Sheet>
    </>
  );
}
